import React, { useState } from 'react';

import styled from '@emotion/styled';

import { COLORS } from '@clutter/clean';

import { Currency } from '@shared/components/helpers';

import { TrackedClick } from '@portal/components/wt/tracked_click';
import { OrderStateEnum, Disposal__CategoryGroupKindEnum } from '@portal/schema';
import {
  isDisposalUnconfirmed,
  getChildDisposalStatusText,
  allowsOpeningDisposalAttachModal,
} from '@portal/utils/disposal';

import { DisposalAttachModal } from '../../disposal_attach_modal';
import { ActionButton, LineItem } from '../../line_item';
import { wtLinkParams } from './constants';

export type OrderWithDisposal = {
  id: string;
  started?: string | null;
  ended?: string | null;
  childDisposal?: {
    id: string;
    state: OrderStateEnum;
    documentHubURL?: string | null;
    estimatedDisposalCost?: number | null;
    estimatedItems?: ReadonlyArray<{
      id: string;
      quantity: number;
      category: {
        id: string;
        name: string;
        defaultCuft?: number | null;
        disposalMinimumPrice?: number | null;
        disposalCategoryGroupKind: Disposal__CategoryGroupKindEnum;
      };
    }>;
  } | null;
  permissions: {
    addDisposal: boolean;
  };
};

type DisposalLineItemProps = {
  order: OrderWithDisposal;
};

const EstimateText = styled.div`
  color: ${COLORS.storm};
  font-size: 12px;
`;

const ACTION_TEXT_CONFIRM = 'Confirm Items';
const ACTION_TEXT_EDIT = 'Edit';

export const DisposalLineItem: React.FC<DisposalLineItemProps> = ({ order }) => {
  const [showModal, setShowModal] = useState(false);

  const childDisposal = order.childDisposal;
  const hasDisposal = childDisposal != null;
  const canAddDisposal = order.permissions.addDisposal;

  // Get disposal data from childDisposal
  const estimatedItems = childDisposal?.estimatedItems ?? [];
  const estimatedCost = childDisposal?.estimatedDisposalCost;

  // Check disposal status
  const isUnconfirmed = isDisposalUnconfirmed(childDisposal);

  if (!canAddDisposal && !hasDisposal) return null;

  const getActionText = () => {
    // No action if disposal can't be managed
    if (!allowsOpeningDisposalAttachModal(childDisposal, order.ended != null)) return null;
    // "Confirm Items" only shows when parent order is completed, disposal is unconfirmed, and documentHubURL exists
    if (isUnconfirmed && order.ended != null && childDisposal?.documentHubURL) return ACTION_TEXT_CONFIRM;
    // Otherwise show "Edit" (before order ends)
    return ACTION_TEXT_EDIT;
  };

  // Show estimate only before order starts (to avoid confusion during order)
  // Once order starts, the estimate is no longer relevant - actual items picked up matter
  const showEstimate = isUnconfirmed && estimatedCost != null && order.started == null;
  const actionText = getActionText();

  return (
    <>
      <LineItem
        label="Disposal Service"
        action={
          actionText ? (
            <TrackedClick
              params={{
                text: actionText,
                trackingParams: {
                  ...wtLinkParams,
                  order_id: order.id,
                  label: actionText,
                  objectName: 'disposal_line_item_action',
                },
              }}
            >
              <ActionButton
                onClick={() => {
                  // If confirming items, go directly to document hub
                  if (actionText === ACTION_TEXT_CONFIRM && childDisposal?.documentHubURL) {
                    window.location.href = childDisposal.documentHubURL;
                  } else {
                    setShowModal(true);
                  }
                }}
              >
                {actionText}
              </ActionButton>
            </TrackedClick>
          ) : undefined
        }
      >
        <div>{getChildDisposalStatusText(childDisposal, order.ended != null)}</div>
        {showEstimate && (
          <EstimateText>
            Est. <Currency value={estimatedCost} />
          </EstimateText>
        )}
      </LineItem>

      <DisposalAttachModal
        parentID={order.id}
        disposalID={childDisposal?.id}
        existingItems={estimatedItems}
        parentOrderStarted={order.started != null}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};
